/**
 * Workspace Scope Switcher
 * 
 * Unified dropdown for workspace selection:
 * - Shows all accessible workspaces (from workspace_members)
 * - Option "All" to aggregate all workspaces
 * - Persists selection to localStorage
 */

import { useState, useEffect } from 'react';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useEntitlements } from '@/hooks/useEntitlements';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Building2, Check, Loader2, Plus, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CreateWorkspaceModal } from './CreateWorkspaceModal';

export function WorkspaceScopeSwitcher() {
  const { 
    mode, 
    activeWorkspaceId, 
    workspaces, 
    setActiveWorkspace, 
    isLoading,
    isSwitchingWorkspace,
    refetchWorkspaces,
  } = useWorkspaceScope();
  const { workspaceLimit, loading: entitlementsLoading } = useEntitlements();
  const [open, setOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const currentCount = workspaces.length;
  const canCreate = currentCount < workspaceLimit;
  const isAtLimit = currentCount >= workspaceLimit;

  // DEV LOGGING: Log what we receive (once per render, dev-only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[WorkspaceScopeSwitcher] 📊 Props received:', {
        mode,
        activeWorkspaceId,
        workspacesCount: workspaces.length,
        workspaces: workspaces.map(w => ({ id: w.workspace_id, name: w.workspace_name })),
        isLoading,
        isSwitchingWorkspace,
      });
    }
  }, [mode, activeWorkspaceId, workspaces.length, isLoading, isSwitchingWorkspace]);

  // Get display name (mode is always 'single' now, 'all' is deprecated)
  const displayName = workspaces.find(w => w.workspace_id === activeWorkspaceId)?.workspace_name || 'Select Workspace';

  // Loading state: show skeleton/placeholder
  if (isLoading || isSwitchingWorkspace) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            disabled
            aria-label="Select workspace"
          >
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isSwitchingWorkspace ? 'Switching workspace...' : 'Loading workspaces...'}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Error state: no workspaces found after loading
  if (workspaces.length === 0 && !isLoading) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => refetchWorkspaces()}
            aria-label="Select workspace"
          >
            <Building2 className="w-4 h-4 text-muted-foreground" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>No workspaces found. Click to retry.</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  const activeWorkspace = workspaces.find(w => w.workspace_id === activeWorkspaceId);
  const tooltipText = activeWorkspace?.workspace_name || 'Select workspace';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              disabled={isSwitchingWorkspace}
              aria-label="Select workspace"
            >
              <Building2 className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Workspace</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* List all workspaces */}
        {workspaces.map((workspace) => {
          const isActive = workspace.workspace_id === activeWorkspaceId;
          return (
            <DropdownMenuItem
              key={workspace.workspace_id}
              onClick={() => {
                if (!isSwitchingWorkspace) {
                  setActiveWorkspace(workspace.workspace_id);
                  setOpen(false);
                }
              }}
              className={cn(
                "cursor-pointer",
                isSwitchingWorkspace && "opacity-50 cursor-not-allowed"
              )}
              disabled={isSwitchingWorkspace}
            >
              <div className="flex items-center justify-between w-full gap-2">
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-medium truncate">
                    {workspace.workspace_name}
                  </span>
                  {isActive && (
                    <span className="text-xs text-muted-foreground">Active</span>
                  )}
                  {!isActive && workspace.role && (
                    <span className="text-xs text-muted-foreground capitalize">
                      {workspace.role}
                    </span>
                  )}
                </div>
                {isActive && (
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                )}
              </div>
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />

        {/* New Workspace button */}
        {canCreate && !entitlementsLoading ? (
          <DropdownMenuItem
            onClick={() => {
              setOpen(false);
              setCreateModalOpen(true);
            }}
            className="cursor-pointer"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span>New workspace</span>
          </DropdownMenuItem>
        ) : isAtLimit && !entitlementsLoading ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <DropdownMenuItem
                  disabled
                  className="cursor-not-allowed opacity-50"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  <span>New workspace</span>
                </DropdownMenuItem>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              <p className="text-sm">
                Workspace limit reached ({currentCount}/{workspaceLimit}).
              </p>
              <p className="text-xs mt-1">
                Upgrade to Pro to create 2 more workspaces.
              </p>
            </TooltipContent>
          </Tooltip>
        ) : null}
      </DropdownMenuContent>

      {/* Create Workspace Modal */}
      <CreateWorkspaceModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={() => {
          refetchWorkspaces();
        }}
      />
    </DropdownMenu>
  );
}

